import { useCallback, useEffect, useRef, useState } from "react";

interface OpenChange {
  open: boolean;
}

/**
 * Open state for a tooltip that waits `delayMs` before opening on keyboard
 * focus too, not only on hover.
 *
 * Zag's tooltip honours `openDelay` for the pointer but opens on focus at
 * once. Drive the tooltip with this hook's `open` / `onOpenChange` (keeping
 * Zag's `openDelay` for the pointer), call `markFocus` from the trigger's
 * focus handler and `markPointer` from its pointer handlers: an open that
 * follows a focus is then held back by `delayMs`, while a pointer open (Zag
 * already waited) and every close apply at once. A caller that controls
 * `open` itself bypasses the hook.
 *
 * Spec: specs/design-system/ledger-primitives.feature
 */
export function useDelayedOpen<T extends OpenChange>({
  delayMs,
  defaultOpen = false,
  onOpenChange,
  stillFocused,
}: {
  delayMs: number;
  defaultOpen?: boolean;
  onOpenChange?: (details: T) => void;
  /**
   * Whether the focus that asked for the open is still there when the delay
   * ends. Zag does not cancel a focus open it never started when the focus
   * moves on (into a menu its trigger opened, say), so the hook checks.
   */
  stillFocused?: () => boolean;
}): {
  open: boolean;
  onOpenChange: (details: T) => void;
  markFocus: () => void;
  markPointer: () => void;
} {
  const [open, setOpen] = useState(defaultOpen);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fromFocus = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  useEffect(() => clear, [clear]);

  const apply = useCallback(
    (details: T) => {
      setOpen(details.open);
      onOpenChange?.(details);
    },
    [onOpenChange],
  );

  const handleChange = useCallback(
    (details: T) => {
      clear();
      if (details.open && fromFocus.current) {
        timer.current = setTimeout(() => {
          if (stillFocused && !stillFocused()) return;
          apply(details);
        }, delayMs);
        return;
      }
      apply(details);
    },
    [apply, clear, delayMs, stillFocused],
  );

  const markFocus = useCallback(() => {
    fromFocus.current = true;
  }, []);
  const markPointer = useCallback(() => {
    fromFocus.current = false;
  }, []);

  return { open, onOpenChange: handleChange, markFocus, markPointer };
}
